import React, { useState, useEffect, useCallback } from 'react';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LoadingSpinner, EmptyState } from '../../components/layout/SharedComponents';
import { Package, Check, Paperclip, Printer, Upload, X, FileText, Plus } from 'lucide-react';
import GrnBatchInput from './GrnBatchInput';
import { useAuth } from '../../contexts/AuthContext';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

const REJECT_REASONS = ['Broken', 'Not Required', 'Shortage', 'Unavailable', 'Wrong Specification'];

export default function GoodsReceivedPanel({ orderNumber, order, fmtTz, fmtTotal, onReload }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [grnSort, handleGrnSort] = useSortState('', 'asc');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [deliveryNoteRef, setDeliveryNoteRef] = useState('');
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [lineInputs, setLineInputs] = useState({});
  const [grnEnabled, setGrnEnabled] = useState(true);
  const [allowOverDelivery, setAllowOverDelivery] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get(`/api/grn/order/${orderNumber}`);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 400 && e.response?.data?.detail?.includes('not enabled')) {
        setGrnEnabled(false);
      }
    }
    setLoading(false);
  }, [orderNumber]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    api.get('/api/grn/settings').then(r => setAllowOverDelivery(r.data.allow_over_delivery)).catch(() => {});
  }, []);

  const canReceive = order && ['ISSUED', 'PARTIAL'].includes(order.status);

  const openReceive = () => {
    const inputs = {};
    (data?.lines || []).forEach(l => {
      if (!l.fully_received) {
        inputs[l.item_id] = {
          qty_received: l.qty_outstanding,
          qty_rejected: 0,
          reject_reason: '',
          reject_action: 'wait',
          reschedule_date: '',
          line_notes: '',
          attachments: [],
          over_delivery_action: '',
        };
      }
    });
    setLineInputs(inputs);
    setDeliveryNoteRef('');
    setReceiveNotes('');
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setReceiveOpen(true);
  };

  const updateLine = (itemId, field, value) => {
    setLineInputs(prev => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
  };

  const handleFileUpload = (itemId, e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setLineInputs(prev => ({
          ...prev,
          [itemId]: {
            ...prev[itemId],
            attachments: [...(prev[itemId]?.attachments || []), { name: file.name, data: reader.result, type: file.type }],
          },
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (itemId, idx) => {
    setLineInputs(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        attachments: prev[itemId].attachments.filter((_, i) => i !== idx),
      },
    }));
  };

  const submitReceipt = async () => {
    const lines = Object.entries(lineInputs)
      .filter(([_, v]) => (parseFloat(v.qty_received) || 0) > 0 || (parseFloat(v.qty_rejected) || 0) > 0)
      .map(([itemId, v]) => ({
        item_id: itemId,
        qty_received: parseFloat(v.qty_received) || 0,
        qty_rejected: parseFloat(v.qty_rejected) || 0,
        reject_reason: v.reject_reason || '',
        reject_action: v.reject_action || 'wait',
        reschedule_date: v.reschedule_date || '',
        line_notes: v.line_notes || '',
        attachments: (v.attachments || []).map(a => ({ name: a.name, data: a.data, type: a.type })),
        over_delivery_action: v.over_delivery_action || '',
        batches: (v.batches || []).filter(b => b.batch_number?.trim()).map(b => ({
          batch_number: b.batch_number.trim(),
          quantity: parseFloat(b.quantity) || 0,
          expiry_date: b.expiry_date || '',
          manufactured_date: b.manufactured_date || '',
          manufacturer: b.manufacturer || '',
          lot_number: b.lot_number || '',
          location_code: (b.location_code || '').toUpperCase(),
          serial_numbers: (b.serial_numbers || []).map(s => s.trim()).filter(s => s),
        })),
      }));

    if (lines.length === 0) { toast.error('No lines to receive'); return; }
    for (const l of lines) {
      if (l.qty_rejected > 0 && !l.reject_reason.trim()) {
        toast.error('Rejection reason is required for all rejected items');
        return;
      }
    }
    // Validate over-delivery (based on PHYSICAL receiving, not net)
    for (const l of lines) {
      const lineData = (data?.lines || []).find(dl => dl.item_id === l.item_id);
      if (lineData) {
        // Physical overage = receiving qty vs what's still expected to physically arrive
        // Outstanding already accounts for previous net received, but for physical check
        // we need: qty_receiving > qty_outstanding + qty_rejected (since outstanding = ordered - net, and net = physical - rejected)
        if (l.qty_received > lineData.qty_outstanding + lineData.qty_rejected) {
          if (!allowOverDelivery) {
            toast.error(`Over-delivery not allowed. Item ${lineData.item_num}: receiving ${l.qty_received} but only ${lineData.qty_outstanding} outstanding. Reduce the receiving quantity.`);
            return;
          }
          if (!l.over_delivery_action) {
            toast.error(`Over-delivery detected on Item ${lineData.item_num}. Please choose to reject overage or add to order.`);
            return;
          }
        }
      }
    }

    setReceiving(true);
    try {
      const r = await api.post(`/api/grn/receive/${orderNumber}`, {
        lines, received_date: receiveDate, delivery_note_ref: deliveryNoteRef, notes: receiveNotes,
      });
      toast.success(r.data.message);

      // If there were rejections, offer to print the returns note
      const hasRejections = lines.some(l => l.qty_rejected > 0);
      if (hasRejections && r.data.grn_number) {
        toast.info('Rejected items detected — you can print a Goods Return Note from the receipt history.');
      }

      setReceiveOpen(false);
      loadStatus();
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Receipt failed')); }
    setReceiving(false);
  };

  const printGRN = (grn) => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>GRN ${grn.grn_number}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial;padding:30px;color:#1a1a1a}
    h1{font-size:20px;margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:12px}
    th,td{border:1px solid #ddd;padding:6px 8px;font-size:12px;text-align:left}
    th{background:#f3f4f6;font-weight:600}.meta{display:flex;gap:24px;margin:12px 0;font-size:12px;color:#555}
    @media print{body{padding:15px}}</style></head><body>
    <h1>Receiving Note: ${grn.grn_number}</h1>
    <p style="font-size:13px;color:#666">Purchase Order: ${grn.order_number}</p>
    <div class="meta">
      <span>Received: ${grn.received_date || '—'}</span>
      <span>Tracking/Vendor Ref: ${grn.delivery_note_ref || '—'}</span>
      <span>Received By: ${grn.received_by || '—'}</span>
    </div>
    ${grn.notes ? `<p style="font-size:12px;margin:8px 0;color:#555">Notes: ${grn.notes}</p>` : ''}
    <table>
      <thead><tr><th>#</th><th>Item Code</th><th>Description</th><th>Ordered</th><th>Received</th><th>Rejected</th><th>Outstanding</th><th>Reason</th><th>Notes</th></tr></thead>
      <tbody>${(grn.lines || []).map(l => `<tr>
        <td>${l.item_num || ''}</td><td>${l.item_code || ''}</td><td>${l.item_description || ''}</td>
        <td style="text-align:right">${l.qty_ordered}</td><td style="text-align:right;font-weight:600">${l.qty_received}</td>
        <td style="text-align:right;color:${l.qty_rejected > 0 ? 'red' : '#999'}">${l.qty_rejected}</td>
        <td style="text-align:right">${l.qty_outstanding}</td><td>${l.reject_reason || '—'}</td>
        <td>${l.line_notes || '—'}</td></tr>`).join('')}
      </tbody>
    </table>
    <p style="margin-top:30px;font-size:11px;color:#999">Printed: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz })}</p>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 200);
  };

  const printReturnsNote = (grn) => {
    const rejectedLines = (grn.lines || []).filter(l => l.qty_rejected > 0);
    if (rejectedLines.length === 0) { toast.info('No rejected items on this receipt'); return; }

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Returns Note — ${grn.grn_number}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial;padding:30px;color:#1a1a1a}
    h1{font-size:20px;margin-bottom:4px;color:#dc2626}h2{font-size:14px;margin:16px 0 8px;color:#333}
    table{width:100%;border-collapse:collapse;margin-top:12px}
    th,td{border:1px solid #ddd;padding:6px 8px;font-size:12px;text-align:left}
    th{background:#fef2f2;font-weight:600;color:#991b1b}
    .meta{display:flex;gap:24px;margin:12px 0;font-size:12px;color:#555}
    .sig{margin-top:40px;display:flex;gap:60px}
    .sig div{flex:1;border-top:1px solid #999;padding-top:4px;font-size:11px;color:#666}
    @media print{body{padding:15px}}</style></head><body>
    <h1>Goods Return Note</h1>
    <p style="font-size:13px;color:#666">Reference: ${grn.grn_number} | Purchase Order: ${grn.order_number}</p>
    <div class="meta">
      <span>Date: ${grn.received_date || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })}</span>
      <span>Tracking/Vendor Ref: ${grn.delivery_note_ref || '—'}</span>
      <span>Processed By: ${grn.received_by || '—'}</span>
    </div>
    <h2>Rejected Items for Return</h2>
    <table>
      <thead><tr><th>#</th><th>Item Code</th><th>Description</th><th>Qty Rejected</th><th>Reason</th><th>Action</th><th>Notes</th></tr></thead>
      <tbody>${rejectedLines.map(l => `<tr>
        <td>${l.item_num || ''}</td><td>${l.item_code || ''}</td><td>${l.item_description || ''}</td>
        <td style="text-align:right;font-weight:600;color:#dc2626">${l.qty_rejected}</td>
        <td>${l.reject_reason || '—'}</td>
        <td>${l.reject_action === 'credit' ? 'Credit Order' : 'Awaiting Redelivery'}</td>
        <td>${l.line_notes || '—'}</td></tr>`).join('')}
      </tbody>
    </table>
    <div class="sig">
      <div>Returned By (signature)</div>
      <div>Received By Supplier (signature)</div>
      <div>Date</div>
    </div>
    <p style="margin-top:30px;font-size:11px;color:#999">Printed: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz })}</p>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 200);
  };

  if (loading) return <LoadingSpinner />;
  if (!grnEnabled) return (
    <div className="text-center py-8">
      <Package className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
      <p className="text-sm font-semibold text-muted-foreground">Receiving Module Not Enabled</p>
      <p className="text-xs text-muted-foreground mt-1">Enable it in System Administration to start receiving goods.</p>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-bold text-primary">Receiving</h3>
        {canReceive && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={openReceive} data-testid="receive-goods-btn">
            <Package className="h-4 w-4 mr-1" /> Receive Goods
          </Button>
        )}
      </div>

      {/* Line status table */}
      {data?.lines?.length > 0 && (
        <table className="w-full text-xs border-collapse mb-4">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left px-2 py-2">#</th>
              <th className="text-left px-2 py-2">Description</th>
              <th className="text-right px-2 py-2">Ordered</th>
              <th className="text-right px-2 py-2">Received</th>
              <th className="text-right px-2 py-2">Rej (Re-delivered)</th>
              <th className="text-right px-2 py-2">Rej (Credited)</th>
              <th className="text-right px-2 py-2">Outstanding</th>
              <th className="text-left px-2 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {sortData(data.lines, grnSort.field, grnSort.dir).map(l => (
              <tr key={l.item_id} className="border-b hover:bg-accent/20">
                <td className="px-2 py-1.5">{l.item_num}</td>
                <td className="px-2 py-1.5 max-w-[200px] truncate">{l.item_description}</td>
                <td className="px-2 py-1.5 text-right">{l.qty_ordered}</td>
                <td className="px-2 py-1.5 text-right font-semibold text-green-700">{l.qty_received || 0}</td>
                <td className="px-2 py-1.5 text-right text-amber-600">{l.qty_rejected_redelivered || 0}</td>
                <td className="px-2 py-1.5 text-right text-red-600">{l.qty_rejected_credited || 0}</td>
                <td className="px-2 py-1.5 text-right font-semibold">{l.qty_outstanding}</td>
                <td className="px-2 py-1.5">
                  {l.fully_received
                    ? <Badge className="bg-green-100 text-green-700 text-[9px]">Complete</Badge>
                    : l.qty_received > 0
                      ? <Badge className="bg-amber-100 text-amber-700 text-[9px]">Partial</Badge>
                      : <Badge className="bg-gray-100 text-gray-600 text-[9px]">Outstanding</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Receipt history */}
      {data?.receipts?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Receipt History</h4>
          <div className="space-y-2">
            {data.receipts.map(grn => {
              const hasRejects = grn.lines?.some(l => l.qty_rejected > 0);
              return (
                <div key={grn.grn_number} className="border rounded-lg p-3 hover:bg-accent/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-sm text-primary font-bold">{grn.grn_number}</span>
                      <span className="text-xs text-muted-foreground ml-2">{grn.received_date}</span>
                      <span className="text-xs text-muted-foreground ml-2">by {grn.received_by}</span>
                      {grn.delivery_note_ref && <span className="text-xs text-muted-foreground ml-2">Ref: {grn.delivery_note_ref}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => printGRN(grn)} title="Print GRN"><Printer className="h-3.5 w-3.5" /></Button>
                      {hasRejects && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => printReturnsNote(grn)} title="Print Returns Note">
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 mt-1 text-[10px] text-muted-foreground">
                    {grn.lines?.map((l, i) => (
                      <span key={i}>Item {l.item_num}: {l.qty_received} recv{l.qty_rejected > 0 ? `, ${l.qty_rejected} rej` : ''}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Receive Dialog */}
      {receiveOpen && (
        <Dialog open onOpenChange={() => setReceiveOpen(false)}>
          <DialogContent className="max-w-[95vw] xl:max-w-5xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-5 pt-5 pb-3">
              <DialogTitle>Receive Goods — {orderNumber}</DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {/* Header fields */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="space-y-1">
                  <Label className="text-xs">Receipt Date</Label>
                  <Input type="date" value={receiveDate} onChange={e => setReceiveDate(e.target.value)} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tracking Number/Vendor Ref #</Label>
                  <Input value={deliveryNoteRef} onChange={e => setDeliveryNoteRef(e.target.value)} className="h-8 text-sm" placeholder="Tracking or vendor reference..." />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notes</Label>
                  <Input value={receiveNotes} onChange={e => setReceiveNotes(e.target.value)} className="h-8 text-sm" placeholder="Optional notes..." />
                </div>
              </div>

              {/* Line items — card layout for each line */}
              <div className="space-y-3">
                {(data?.lines || []).filter(l => !l.fully_received).map(l => {
                  const inp = lineInputs[l.item_id] || {};
                  const isRejecting = (parseFloat(inp.qty_rejected) || 0) > 0;
                  const hasOutstanding = l.qty_outstanding > (parseFloat(inp.qty_received) || 0);

                  return (
                    <div key={l.item_id} className="border rounded-lg p-3 bg-card">
                      {/* Line header */}
                      <div className="flex items-center gap-3 mb-2">
                        <Badge variant="outline" className="text-xs font-mono">{l.item_num}</Badge>
                        <span className="text-sm font-medium flex-1 truncate">{l.item_description}</span>
                        {l.qty_received > 0 && <span className="text-xs text-green-600">Prev Received: {l.qty_received}</span>}
                      </div>

                      {/* Input row: Ordered | Receiving | Rejecting | Balance | Reason | Action | Reschedule | Attach */}
                      <div className="grid grid-cols-8 gap-1.5 items-end">
                        <div className="space-y-0.5">
                          <Label className="text-[10px] text-muted-foreground font-semibold">Ordered</Label>
                          <div className="h-8 flex items-center justify-end rounded-lg border border-input bg-muted/30 px-2 text-sm font-semibold">{l.qty_ordered}</div>
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px] text-green-700 font-semibold">Receiving</Label>
                          <Input type="number" min="0" max={!allowOverDelivery ? l.qty_outstanding : undefined}
                            value={inp.qty_received ?? ''} onChange={e => updateLine(l.item_id, 'qty_received', e.target.value)}
                            className="h-8 text-xs text-right" />
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px] text-red-600 font-semibold">Rejecting</Label>
                          <Input type="number" min="0" value={inp.qty_rejected ?? ''} onChange={e => updateLine(l.item_id, 'qty_rejected', e.target.value)}
                            className="h-8 text-xs text-right" />
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px] text-blue-600 font-semibold">Booking In</Label>
                          <div className="h-8 flex items-center justify-end rounded-lg border border-input bg-muted/30 px-2 text-xs font-semibold">
                            {Math.max(0, (parseFloat(inp.qty_received) || 0) - (parseFloat(inp.qty_rejected) || 0))}
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px]">Reject Reason</Label>
                          <select value={inp.reject_reason || ''} onChange={e => updateLine(l.item_id, 'reject_reason', e.target.value)}
                            disabled={!isRejecting}
                            className={`h-8 w-full rounded-lg border border-input bg-background px-2 text-xs ${!isRejecting ? 'opacity-40' : ''}`}>
                            <option value="">Select reason...</option>
                            {REJECT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px]">If Rejected</Label>
                          <select value={inp.reject_action || 'wait'} onChange={e => updateLine(l.item_id, 'reject_action', e.target.value)}
                            disabled={!isRejecting}
                            className={`h-8 w-full rounded-lg border border-input bg-background px-2 text-xs ${!isRejecting ? 'opacity-40' : ''}`}>
                            <option value="wait">Wait for redelivery</option>
                            <option value="credit">Credit order</option>
                          </select>
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px]">Reschedule</Label>
                          <Input type="date" value={inp.reschedule_date || ''} onChange={e => updateLine(l.item_id, 'reschedule_date', e.target.value)}
                            disabled={!hasOutstanding} className={`h-8 text-xs ${!hasOutstanding ? 'opacity-40' : ''}`} />
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-[10px]">Attach</Label>
                          <div className="h-8 flex items-center rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-2 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
                            onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
                            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); handleFileUpload(l.item_id, { target: { files: e.dataTransfer.files } }); }}
                            onClick={() => document.getElementById(`file-${l.item_id}`)?.click()}>
                            <Paperclip className="h-3 w-3 text-muted-foreground mr-1" />
                            <span className="text-[10px] text-muted-foreground">{(inp.attachments || []).length ? `${(inp.attachments || []).length} file(s)` : 'Drop or click'}</span>
                            <input id={`file-${l.item_id}`} type="file" className="hidden" accept="image/*,.pdf" multiple onChange={e => handleFileUpload(l.item_id, e)} />
                          </div>
                        </div>
                      </div>

                      {/* Attachments preview */}
                      {(inp.attachments || []).length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {inp.attachments.map((a, i) => (
                            <div key={i} className="flex items-center gap-1 bg-muted/30 rounded px-2 py-0.5 text-[10px]">
                              <Paperclip className="h-2.5 w-2.5" />
                              <span className="max-w-[100px] truncate">{a.name}</span>
                              <button onClick={() => removeAttachment(l.item_id, i)} className="text-red-500 hover:text-red-700"><X className="h-2.5 w-2.5" /></button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Over-delivery action */}
                      {(() => {
                        const physReceiving = parseFloat(inp.qty_received) || 0;
                        const isOver = physReceiving > l.qty_outstanding;
                        if (!isOver || !allowOverDelivery) return null;
                        const overage = physReceiving - l.qty_outstanding;
                        return (
                          <div className="mt-2 p-2 border border-amber-300 bg-amber-50 rounded-lg">
                            <p className="text-xs font-semibold text-amber-700 mb-1">Over-delivery detected: +{overage} more than ordered</p>
                            <div className="flex gap-2">
                              <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer text-xs ${inp.over_delivery_action === 'reject_overage' ? 'border-red-400 bg-red-50' : 'border-muted hover:bg-accent/30'}`}>
                                <input type="radio" name={`over-${l.item_id}`} checked={inp.over_delivery_action === 'reject_overage'} onChange={() => updateLine(l.item_id, 'over_delivery_action', 'reject_overage')} />
                                <span>Reject the overage ({overage})</span>
                              </label>
                              <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer text-xs ${inp.over_delivery_action === 'add_to_order' ? 'border-green-400 bg-green-50' : 'border-muted hover:bg-accent/30'}`}>
                                <input type="radio" name={`over-${l.item_id}`} checked={inp.over_delivery_action === 'add_to_order'} onChange={() => updateLine(l.item_id, 'over_delivery_action', 'add_to_order')} />
                                <span>Add to order (+{overage})</span>
                              </label>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Line notes */}
                      <div className="mt-2">
                        <Input value={inp.line_notes || ''} onChange={e => updateLine(l.item_id, 'line_notes', e.target.value)}
                          className="h-7 text-xs" placeholder="Notes for this line item..." />
                      </div>

                      {/* ── Batch capture for batch-tracked items ── */}
                      {l.batch_tracked && (parseFloat(inp.qty_received) || 0) > 0 && (
                        <GrnBatchInput line={l} lineInput={inp} onUpdateLine={updateLine} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex justify-start gap-2 mt-4 pt-3 border-t">
                <Button className="bg-green-600 hover:bg-green-700" onClick={submitReceipt} disabled={receiving}>
                  <Check className="h-4 w-4 mr-1" /> {receiving ? 'Processing...' : 'Confirm Receipt'}
                </Button>
                <Button variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
